/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useRef, useCallback } from "react"
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
} from "@material-ui/core"
import { Crop, RotateRight } from "@material-ui/icons"

/**
 * Converts a crop (which may be in "%" units) to pixel values,
 * then draws the rotated image onto a canvas and extracts the cropped region.
 *
 * The rotation is applied to the FULL image first, then the crop window
 * is extracted from the rotated result — this is the correct order.
 */
const getCroppedImg = (image, crop, rotation = 0) =>
  new Promise(resolve => {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    // ── 1. Normalize crop to natural pixels (handles both "%" and "px") ───
    const cropX =
      crop.unit === "%" ? (crop.x / 100) * image.naturalWidth : crop.x
    const cropY =
      crop.unit === "%" ? (crop.y / 100) * image.naturalHeight : crop.y
    const cropW =
      crop.unit === "%" ? (crop.width / 100) * image.naturalWidth : crop.width
    const cropH =
      crop.unit === "%"
        ? (crop.height / 100) * image.naturalHeight
        : crop.height

    if (rotation === 0) {
      // ── Fast path: no rotation ──────────────────────────────────────────
      canvas.width = cropW
      canvas.height = cropH
      ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH)
    } else {
      // ── 2. Render full rotated image onto an offscreen canvas ───────────
      const radians = (rotation * Math.PI) / 180
      const sinA = Math.abs(Math.sin(radians))
      const cosA = Math.abs(Math.cos(radians))
      const rotatedW = image.naturalWidth * cosA + image.naturalHeight * sinA
      const rotatedH = image.naturalWidth * sinA + image.naturalHeight * cosA

      const rotCanvas = document.createElement("canvas")
      rotCanvas.width = rotatedW
      rotCanvas.height = rotatedH
      const rotCtx = rotCanvas.getContext("2d")

      rotCtx.translate(rotatedW / 2, rotatedH / 2)
      rotCtx.rotate(radians)
      rotCtx.drawImage(
        image,
        -image.naturalWidth / 2,
        -image.naturalHeight / 2,
        image.naturalWidth,
        image.naturalHeight
      )

      // The crop coords are relative to the original image space.
      // Offset by the extra space added when the bounding box expanded.
      const offsetX = (rotatedW - image.naturalWidth) / 2
      const offsetY = (rotatedH - image.naturalHeight) / 2

      canvas.width = cropW
      canvas.height = cropH
      ctx.drawImage(
        rotCanvas,
        cropX + offsetX,
        cropY + offsetY,
        cropW,
        cropH,
        0,
        0,
        cropW,
        cropH
      )
    }

    canvas.toBlob(
      blob => {
        if (!blob) return
        const file = new File([blob], "cropped.jpg", { type: "image/jpeg" })
        resolve({ file, url: URL.createObjectURL(blob) })
      },
      "image/jpeg",
      0.9
    )
  })

const makeInitialCrop = (mediaWidth, mediaHeight, aspect) =>
  centerCrop(
    makeAspectCrop({ unit: "%", width: 80 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  )

const ImageCropDialog = ({ open, imageSrc, onClose, onCropComplete }) => {
  const imgRef = useRef(null)
  const [crop, setCrop] = useState(undefined)
  const [rotation, setRotation] = useState(0)
  const [aspect, setAspect] = useState(1)
  const [loading, setLoading] = useState(false)

  const ASPECT_OPTIONS = [
    { label: "1:1", value: 1 },
    { label: "4:3", value: 4 / 3 },
    { label: "3:4 (Retrato)", value: 3 / 4 },
    { label: "Libre", value: undefined },
  ]

  const onImageLoad = useCallback(
    e => {
      const { naturalWidth: w, naturalHeight: h } = e.currentTarget
      setCrop(makeInitialCrop(w, h, aspect || 1))
      setRotation(0)
    },
    [aspect]
  )

  const handleAspectChange = value => {
    setAspect(value)
    if (imgRef.current) {
      const { naturalWidth: w, naturalHeight: h } = imgRef.current
      setCrop(makeInitialCrop(w, h, value || 1))
    }
  }

  const handleConfirm = async () => {
    if (!imgRef.current || !crop) return
    setLoading(true)
    try {
      const result = await getCroppedImg(imgRef.current, crop, rotation)
      onCropComplete(result.file, result.url)
      onClose()
    } catch (e) {
      // Error al cortar
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setRotation(0)
    if (imgRef.current) {
      const { naturalWidth: w, naturalHeight: h } = imgRef.current
      setCrop(makeInitialCrop(w, h, aspect || 1))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 12 } }}
    >
      <DialogTitle style={{ backgroundColor: "#092147", padding: "12px 24px" }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Crop style={{ fontSize: 20, color: "white" }} />
          <Typography variant="h6" style={{ fontWeight: 600, color: "white" }}>
            Recortar Imagen
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: 0, backgroundColor: "#1a1a2e" }}>
        {/* ── Crop area ── */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{
            minHeight: 380,
            maxHeight: 420,
            overflow: "auto",
            backgroundColor: "#111",
            padding: 12,
          }}
        >
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={aspect}
              style={{ maxHeight: 400 }}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Recortar"
                onLoad={onImageLoad}
                style={{
                  maxHeight: 390,
                  maxWidth: "100%",
                  transform: `rotate(${rotation}deg)`,
                  transition: "transform 0.2s",
                }}
              />
            </ReactCrop>
          )}
        </Box>

        {/* ── Controls ── */}
        <Box p={3} style={{ backgroundColor: "#f8fafc" }}>
          {/* Aspect ratio */}
          <Box mb={2}>
            <Typography
              variant="caption"
              style={{
                color: "#64748b",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Proporción
            </Typography>
            <Box display="flex" style={{ gap: 8 }} flexWrap="wrap">
              {ASPECT_OPTIONS.map(opt => (
                <Button
                  key={opt.label}
                  size="small"
                  variant={aspect === opt.value ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => handleAspectChange(opt.value)}
                  style={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    padding: "2px 10px",
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Rotation */}
          <Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={0.5}>
              <RotateRight style={{ fontSize: 18, color: "#64748b" }} />
              <Typography
                variant="caption"
                style={{ color: "#64748b", fontWeight: 600 }}
              >
                Rotación: {rotation}°
              </Typography>
            </Box>
            <Slider
              value={rotation}
              min={-180}
              max={180}
              step={1}
              onChange={(_, v) => setRotation(v)}
              style={{ color: "#1976d2" }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        style={{
          padding: "12px 24px",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Button
          onClick={handleReset}
          style={{ textTransform: "none", color: "#64748b" }}
        >
          Restablecer
        </Button>
        <Box flex={1} />
        <Button
          onClick={onClose}
          style={{ textTransform: "none" }}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading || !crop}
          startIcon={<Crop style={{ fontSize: 16 }} />}
          style={{ textTransform: "none", fontWeight: 600 }}
        >
          {loading ? "Recortando..." : "Aplicar Recorte"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImageCropDialog
